import { CurrencyPipe, NgClass, NgStyle } from "@angular/common";
import {Component, Input, OnInit, inject, SecurityContext} from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatCell, MatCellDef, MatColumnDef, MatFooterCell, MatFooterCellDef, MatFooterRow, MatFooterRowDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from "@angular/material/table";
import { ClassDirective, StyleDirective } from "@ngbracket/ngx-layout/extended";
import { LayoutAlignDirective, LayoutDirective, FlexFillDirective } from "@ngbracket/ngx-layout/flex";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { isErroredExecutionResult, isHttpResponseError } from "@tylertech/core-module";
import { ForgeCardModule, ForgeLinearProgressModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { MetadataActionHelpers, MetadataActionService } from "@tylertech/metadata-module";
import { IInfoCardAction, IInfoCardSearchCriteria, IInfoTable, IInfoTableElement, IInfoTableRow, IMetadataAction, InfoCardActionType } from "@tylertech/tyler-api-types";
import { InfoCardService } from "../../services";
import { DomSanitizer } from "@angular/platform-browser";

type TableRow = Record<"$row", IInfoTableRow> | Record<string, IInfoTableElement>;

@Component({
    selector: "info-card-detail-table",
    templateUrl: "./detail.table.tmpl.html",
    styleUrls: [
        "./detail.table.component.scss"
    ],
    imports: [
        CommandBarComponent,
        CurrencyPipe,
        ClassDirective,
        LayoutAlignDirective,
        LayoutDirective,
        StyleDirective,
        FlexFillDirective,
        ForgeCardModule,
        ForgeLinearProgressModule,
        ForgeTooltipModule,
        MatCell,
        MatCellDef,
        MatColumnDef,
        MatFooterCell,
        MatFooterCellDef,
        MatFooterRow,
        MatFooterRowDef,
        MatHeaderCell,
        MatHeaderCellDef,
        MatHeaderRow,
        MatHeaderRowDef,
        MatIcon,
        MatRow,
        MatRowDef,
        MatTable,
        NgClass,
        NgStyle,

    ]
})
export class DetailTableComponent implements OnInit
{
    private readonly $intentService = inject(IntentService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $infoCardService = inject(InfoCardService);

    @Input() readonly cardProviderId: string;
    @Input() readonly itemId: string;

    private _hasCommands: boolean;
    private table: IInfoTable;

    readonly tableData: MatTableDataSource<TableRow>;
    dataColumns: ReadonlyArray<string>;
    columns: ReadonlyArray<string>;
    isLoaded: boolean;
    loadFailed: boolean;
    loadFailedMessage: string;

    private sanitizer = inject(DomSanitizer);

    constructor()
    {
        this.tableData = new MatTableDataSource<TableRow>();
    }

    get hasCommands(): boolean
    {
        if (this._hasCommands)
        {
            return this._hasCommands;
        }

        for (let i = 0; i < this.table.Rows.length; i++)
        {
            if (this.table.Rows[i].Commands && this.table.Rows[i].Commands.length > 0)
            {
                this._hasCommands = true;
                return true;
            }
        }

        this._hasCommands = false;
        return false;
    }

    get hasItems(): boolean
    {
        return this.table?.Rows?.length > 0 || this.tableData.data?.length > 0;
    }

    get title(): string | undefined
    {
        return this.table.Title;
    }

    ngOnInit(): void
    {
        const criteria: IInfoCardSearchCriteria = {
            Id: this.itemId,
            CardProviderId: this.cardProviderId,
            Criteria: undefined
        };
        this.$infoCardService.GetDetailTable(criteria)
            .then(
                (result: IInfoTable) =>
                {
                    // TODO - why isn't this done on the backend?
                    result.ProviderId = this.itemId + ".DetailTableProvider";
                    this.table = result;

                    this.tableData.data = this.table.Rows.map(x =>
                    {
                        return x.Elements.reduce(
                            (row: TableRow, element: IInfoTableElement) =>
                            {
                                row[element.Label] = element;
                                // TODO - manage cell value formatting here?
                                return row;
                            },
                            {
                                $row: x
                            });
                    });

                    // use Name?
                    this.dataColumns = [
                        ...this.table.Rows[0].Elements.map(x => x.Label), // use Name?
                    ];

                    // TODO - commands?
                    if (this.table.Rows[0].Commands?.length > 0)
                    {
                        this.columns = [...this.dataColumns, "$$commands"];
                    }
                    else
                    {

                        this.columns = this.dataColumns;
                    }
                },
                (error: unknown) =>
                {
                    this.table = null;
                    this.loadFailed = true;
                    this.loadFailedMessage = isHttpResponseError(error)
                        ? error.error.Message
                        : isErroredExecutionResult(error)
                            ? error.ErrorMessage
                            : "Failed to load data";
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    getRowClasses(row: IInfoTableRow): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "show-separator-above": row.HasRowSeparator
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    getElementStyle(element: IInfoTableElement): Record<string, string>
    {

        return {
            'width': this.truncateRightWidth(element)
        };

    }

    private truncateRightWidth(element: IInfoTableElement): string
    {
        return this.isRightAligned(element) ? "100%" : "95%";
    }

    private isRightAligned(element: IInfoTableElement): boolean
    {
        if (element.IgnoreNumber)
        {
            return false;
        }

        if (!element.Value)
        {
            return false;
        }

        const dt = Date.parse(element.Value);
        const elementValue = parseFloat(element.Value.replace(/[$,()]/g, ""));
        return (!isNaN(elementValue) && typeof elementValue === "number") || !isNaN(dt);
    }
    
    SanitizeValue(value: string): string {
        return this.sanitizer.sanitize(SecurityContext.HTML,value) ?? "";
    }

    getColumnClasses(columnNameOrElement: string | IInfoTableElement): Record<string, boolean>
    {
        const isCell = typeof columnNameOrElement !== "string";
        const element = isCell
            ? columnNameOrElement
            : this.getColumnElement(columnNameOrElement);
        const classes = {
            numeric: this.isRightAligned(element),
        };

        if (!isCell && element.Name)
        {
            // TODO - What is this custom class name for?
            classes[element.Name.replace(/ /g, "-")] = true;
        }

        return classes;
    }

    isBoolean(element: IInfoTableElement): boolean
    {
        if (!element.Value)
        {
            return false;
        }
        return /true|false/.test(element.Value.toLowerCase());
    }

    isTrue(element: IInfoTableElement): boolean
    {
        return /true/.test(element.Value?.toLowerCase());
    }

    getTotal(column: string): number
    {
        const element = this.getColumnElement(column);
        if (!element?.HasTotal)
        {
            return 0;
        }

        let sum = 0;
        // TODO - reduce
        this.table.Rows.forEach(row =>
        {
            const rowElement = row.Elements.find(e => e.Label === element.Label);
            if (rowElement?.Value)
            {
                // TODO - formatNumber?
                sum += parseFloat(rowElement.Value
                    .replace(/[$,)]/g, "")
                    .replace(/[(]/, "-"));
            }
        });
        return sum;
    }

    private getColumnElement(column: string): IInfoTableElement | undefined
    {
        return this.table.Rows[0].Elements.find(x => x.Label === column);
    }

    commands: Record<string, ReadonlyArray<Command>> = {};

    // TODO - should this be memoized?
    getCommands(tableRow: TableRow): ReadonlyArray<Command>
    {
        const infoTableRow = tableRow.$row as IInfoTableRow;
        if (this.commands[infoTableRow.Id])
            return this.commands[infoTableRow.Id];
        this.commands[infoTableRow.Id] = infoTableRow.Commands.reduce(
            (commands: Array<Command>, action: IInfoCardAction) =>
            {
                if (action.ActionType === InfoCardActionType.Icon)
                {
                    commands.push(
                        new IconCommand({
                            icon: action.Icon,
                            tooltip: action.Label,
                            action: () => this.executeCommand(infoTableRow, action),
                            getEnabled: () => action.Enabled,
                            getVisible: () => action.Visible,
                        }));
                }

                if (action.Action === "Metadata")
                {
                    // TODO - Why do this need to be done now, and stored on a custom field? Why can't we create the repositoryFormData when the action is executed?
                    // We even reload the whole page immediately after the action is executed.
                    const metadataAction: IMetadataAction = JSON.parse(action.Data[0]);
                    infoTableRow["$Loaded" + action.Label] = this.$metadataActionService.GetNewRepositoryFormData(metadataAction);
                }

                return commands;
            },
            []);
        return this.commands[infoTableRow.Id];
    }

    private executeCommand(row: IInfoTableRow, command: IInfoCardAction): void
    {
        if (command.Action === "Metadata")
        {
            const action = JSON.parse(command.Data[0]);
            const repositoryFormData = row["$Loaded" + command.Label];
            this.$metadataActionService.ExecuteAction(undefined, action, repositoryFormData)
                .then((result: unknown) =>
                {
                    if (!MetadataActionHelpers.IsCancelObject(result))
                    {
                        location.reload();
                    }
                });
            return;
        }

        const parms: Record<string, string> = {};
        command.Data.forEach((value, index) =>
        {
            if (index % 2 === 0)
            {
                parms[value] = command.Data[index + 1];
            }
        });
        this.$intentService.resolve(command.MimeType, command.Action, parms);
    }
}
